import { useState } from 'react';
import { HelpCircle, MessageCircleQuestion, Users, ListOrdered, ChevronDown, ChevronUp, CheckCircle, FileText, GraduationCap, Trash, Pencil, Copy, Calendar, Clock, FileJson, Check, X } from 'lucide-react';
import Button from '../../../components/Button';
import { ButtonWithConfirmation } from '../../../components/ButtonWithConfirmation';
import { EditQuestionModal } from './EditQuestionModal';
import type { CommunityQuestion } from '../types/community-questions';

interface QuestionCardProps {
  question: CommunityQuestion;
  onDelete: (id: string) => void;
  onEdit: (updatedQuestion: CommunityQuestion) => void;
  onDuplicate: (id: string) => void;
  isSelected?: boolean;
  onToggleSelection?: (id: string) => void;
}

export const QuestionCard = ({ question, onDelete, onEdit, onDuplicate, isSelected = false, onToggleSelection }: QuestionCardProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [copyJsonStatus, setCopyJsonStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const getTypeIcon = (): React.ReactNode => {
    const iconClass = "w-4 h-4";
    return question.type === 'question'
      ? <HelpCircle className={iconClass} />
      : <MessageCircleQuestion className={iconClass} />;
  };

  const formatDate = (date: Date): string => {
    const dateObj = date instanceof Date ? date : new Date(date);
    return dateObj.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleEditSuccess = (updatedQuestion: CommunityQuestion, isEdit: boolean) => {
    setIsEditModalOpen(false);
    if (isEdit) {
      onEdit(updatedQuestion);
    }
  };

  const handleCopyJson = async () => {
    try {
      const jsonString = JSON.stringify(question, null, 2);
      await navigator.clipboard.writeText(jsonString);
      setCopyJsonStatus('success');
      setTimeout(() => setCopyJsonStatus('idle'), 2000);
    } catch {
      setCopyJsonStatus('error');
      setTimeout(() => setCopyJsonStatus('idle'), 2000);
    }
  };

  return (
    <>
      <div className={`border-2 rounded-lg p-3 space-y-2 bg-white transition-colors ${isSelected ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}`}>
        {/* Header with Edit and Delete Buttons */}
        <div className="flex justify-between items-start gap-3">
          <div className="flex items-center gap-2 flex-1">
            {onToggleSelection && (
              <input
                type="checkbox"
                checked={isSelected}
                onChange={() => onToggleSelection(question.id)}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                title="Select for export"
              />
            )}
            <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium ${question.type === 'question' ? 'bg-blue-100 text-blue-800' : 'bg-purple-300 text-purple-800'
              }`}>
              {getTypeIcon()}
              {question.type}
            </span>
          </div>
          <div className="flex items-center gap-1">
            <Button
              onClick={handleCopyJson}
              variant={copyJsonStatus === 'success' ? 'primary' : 'ghost'}
              size="sm"
              icon={
                copyJsonStatus === 'success' ? (
                  <Check className="w-4 h-4" />
                ) : copyJsonStatus === 'error' ? (
                  <X className="w-4 h-4" />
                ) : (
                  <FileJson className="w-4 h-4" />
                )
              }
              title={
                copyJsonStatus === 'success'
                  ? 'JSON copied!'
                  : copyJsonStatus === 'error'
                    ? 'Failed to copy'
                    : 'Copy JSON to clipboard'
              }
            />
            <Button
              onClick={() => onDuplicate(question.id)}
              variant="ghost"
              size="sm"
              icon={<Copy className="w-4 h-4" />}
              title="Duplicate question"
            />
            <Button
              onClick={() => setIsEditModalOpen(true)}
              variant="ghost"
              size="sm"
              icon={<Pencil className="w-4 h-4" />}
              title="Edit question"
            />
            <ButtonWithConfirmation
              onClick={() => onDelete(question.id)}
              firstIcon={<Trash className="w-4 h-4" />}
              secondIcon={<Trash className="w-4 h-4" />}
              firstVariant="ghost"
              secondVariant="danger"
              size="sm"
              firstText=""
              secondText="Really delete?"
            />
          </div>
        </div>

        {/* Question Text */}
        <div className="p-2">
          <p className="text-base font-semibold text-gray-900 break-words">{question.text}</p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 px-2">
          <label className="flex items-center gap-1.5 text-xs font-medium text-gray-500 mb-1">
            <Users className="w-3.5 h-3.5" />
            Responses: {question.totalResponses}
          </label>
          <label className="flex items-center gap-1.5 text-xs font-medium text-gray-500 mb-1">
            <ListOrdered className="w-3.5 h-3.5" />
            Options: {Object.keys(question.options).length}
          </label>
          <label className="flex items-center gap-1.5 text-xs text-gray-500">
            <Calendar className="w-3.5 h-3.5" />
            Created: {formatDate(question.createdAt)}
          </label>
          <label className="flex items-center gap-1.5 text-xs text-gray-500">
            <Clock className="w-3.5 h-3.5" />
            Updated: {formatDate(question.updatedAt)}
          </label>
        </div>

        {/* Expandable content */}
        {isExpanded && (
          <div className="space-y-3 pt-2 border-t border-gray-200">
            {question.description && (
              <div className="p-2">
                <label className="flex items-center gap-1.5 text-xs font-medium text-gray-500 mb-1">
                  <FileText className="w-3.5 h-3.5" />
                  Description
                </label>
                <p className="text-sm text-gray-700">{question.description}</p>
              </div>
            )}

            {question.learning && (
              <div className="p-2">
                <label className="flex items-center gap-1.5 text-xs font-medium text-gray-500 mb-1">
                  <GraduationCap className="w-3.5 h-3.5" />
                  Learning
                </label>
                <p className="text-sm text-gray-700 whitespace-pre-wrap">{question.learning}</p>
              </div>
            )}

            {/* Options */}
            <div className="p-2">
              <label className="flex items-center gap-1.5 text-xs font-medium text-gray-500 mb-2">
                <ListOrdered className="w-3.5 h-3.5" />
                Options
              </label>
              <ul className="space-y-2">
                {question.optionsOrder.map((optionId) => {
                  const option = question.options[optionId];
                  if (!option) return null;
                  return (
                    <li key={optionId} className="flex items-center gap-2 text-sm p-2 bg-gray-50 rounded">
                      <span className="w-6 h-6 rounded-full bg-white border border-gray-200 flex items-center justify-center text-xs font-medium text-gray-700">
                        {optionId}
                      </span>
                      <span className="flex-1 min-w-0 break-words text-gray-900">{option.label}</span>
                      {question.type === 'question' && option.isCorrect && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-800 text-xs font-medium rounded">
                          <CheckCircle className="w-3 h-3" />
                          Correct
                        </span>
                      )}
                      <span className="text-xs text-gray-500 font-medium">({option.responseCount})</span>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        )}

        {/* Expand/Collapse Button */}
        <div className="pt-2 border-t border-gray-200">
          <Button
            onClick={() => setIsExpanded(!isExpanded)}
            variant="ghost"
            size="sm"
            icon={isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          >
            {isExpanded ? 'Show less' : 'Show more'}
          </Button>
        </div>
      </div>

      {/* Edit Modal */}
      <EditQuestionModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        question={question}
        onSuccess={handleEditSuccess}
      />
    </>
  );
};
